import React, { ChangeEvent, Dispatch, useEffect, useMemo, useRef, useState } from 'react'
import { GroupDataRow } from '../earlyWarning/emailComp/EmailTypes'
import { GetSelectedGroup } from './WarningWorkflowProcess';

type GroupSelectTablePropsType = {
    selectedGroup: GetSelectedGroup[];
    setSelectedGroup: Dispatch<React.SetStateAction<GetSelectedGroup[]>>;
    type: 'INTERNAL' | 'EXTERNAL' | 'ACK';
    token?: string;
}

const GroupSelectTable = (props: GroupSelectTablePropsType) => {


    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const allGroups = useRef<GroupDataRow[]>([]);

    const [filteredData, setFilteredData] = useState<GroupDataRow[]>([])
    const [slicedData, setSlicedData] = useState<GroupDataRow[][]>([])

    const [initialSelectedCodes, setInitialSelectedCodes] = useState<Set<string>>(new Set());
    const hasInitializedFromProps = useRef(false);

    const getGroupIdList = (groups: GetSelectedGroup[])  => {
        const data: string[] = []
        groups.forEach(item => {
            data.push(item.groupId)
        })
        return data
    }

    // Initialize from props ONLY once when component mounts
    useEffect(() => {
        if (hasInitializedFromProps.current) return;
        
        // Set initial selected codes from props
        const initialGroupIds = getGroupIdList(props.selectedGroup);
        setSelectedCodes(new Set(initialGroupIds));
        setInitialSelectedCodes(new Set(initialGroupIds));
        
        hasInitializedFromProps.current = true;
        
    }, []); // Empty dependency array to run only once on mount

    // Keep selectedCodes in sync with parent updates (but NOT initialSelectedCodes)
    useEffect(() => {
        if (hasInitializedFromProps.current) {
            setSelectedCodes(new Set(getGroupIdList(props.selectedGroup)));
        }
    }, [props.selectedGroup]);

    const getAllGroups = () => {
        const token = localStorage.getItem("access_token");

        if (props.type === 'INTERNAL')
        {
            fetch(BASE_URL + "/ew/api/v1/getallgroup", {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    allGroups.current = data;
                    setFilteredData(data);
                })
                .catch((err) => console.error("Error fetching roles:", err));
        }
        else 
        {
            fetch(`${BASE_URL}/jwt-auth/api/v1/getallgroup?token=${props.token}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    allGroups.current = data;
                    setFilteredData(data);
                })
                .catch((err) => console.error("Error fetching roles:", err));
        }
    }

    useEffect(() => {
        getAllGroups();
    }, [])

    useEffect(() => {
        const sliced = paginationList(filteredData, sliceCount);
        setSlicedData(sliced);
        setSelectedPage(0);
    }, [filteredData, sliceCount])

    const paginationList = (data: GroupDataRow[], size: number): GroupDataRow[][] => {
        const result: GroupDataRow[][] = []

        if (size >= data.length) {
            return [data]
        }

        let start = 0

        while (start < data.length) {
            result.push(data.slice(start, start + size))
            start += size
        }

        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
            }

        } else if (side === "prev") {
            if (0 <= selectedPage - 1) {
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === '') {
            setFilteredData(allGroups.current)
        } else {
            const query = event.target.value.toLowerCase();
            setFilteredData(allGroups.current.filter(item =>
                item.groupname.toLowerCase().includes(query) ||
                item.groupid.toLowerCase().includes(query)
            ))
        }
    }

    const handleSelectGroup = (event: ChangeEvent<HTMLInputElement>) => {
        const groupId = event.target.name;
        const isChecked = event.target.checked;
        
        // Check if this is an initial selection
        const isInitialSelection = initialSelectedCodes.has(groupId);
        
        // If it's an initial selection, don't allow toggling
        if (isInitialSelection) {
            return;
        }

        setSelectedCodes(prev => {
            const updated = new Set(prev);
            if (isChecked) {
                updated.add(groupId);
            } else {
                updated.delete(groupId);
            }
            return updated;
        });
    
        // Update the parent state based on the new selection
        props.setSelectedGroup(prevState => {
            if (isChecked) {
                // Add the new group if checked
                return [
                    ...prevState,
                    {
                        groupId: groupId,
                        addUser: ""
                    }
                ];
            } else {
                // Remove the group if unchecked
                return prevState.filter(group => group.groupId !== groupId);
            }
        });
    }

    return (
        <div className='subTable-wrapper' style={{width: 'fit-content', flex: '1'}}>
            <span className='main-title'>Select Subscribed Groups</span>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} value={sliceCount} min={1} onChange={(e) => handleSliceCountChange(e)} /> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here' /></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{ gridTemplateColumns: `1fr 1fr 4rem`, width: '100%' }}>
                        <span>Group</span>
                        <span>Department Name</span>
                        <span>Select</span>
                    </div>
                </div>
                {slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                    <div key={index} className='table-wrapper'>
                        <div className='table-row' style={{ gridTemplateColumns: `1fr 1fr 4rem`, width: '100%' }}>
                            <span>{row.groupname}</span>
                            <span>{row.departmentName}</span>
                            <span style={{ justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name={row.groupid}
                                    onChange={(e) => handleSelectGroup(e)}
                                    checked={selectedCodes.has(row.groupid)}
                                    disabled={initialSelectedCodes.has(row.groupid)}
                                />
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            {filteredData.length === 0 && (
                <div className='table-no-data-wrapper'>
                    <span>no any data available</span>
                </div>
            )}
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default GroupSelectTable
